import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * フォームの状態を表す型
 */
type FormState = {
    license: string;
    yy: number;
    mm: number;
    dd: number;
    men: number;
    women: number;
    breakfast: boolean;
    lunch: boolean;
    dinner: boolean;
    couple: boolean;
};

/**
 * 料金プランの月選択肢を表す型
 */
type PlanMonth = {
    yy: number;
    mm: number;
    label: string;
};

/**
 * エクマ申込みサイトのベースURL
 *
 * エクマの改修がテストページにしか反映されていないためテストページを参照している。
 * 本番へ反映されたら下記に差し替える(スラッグは3校で異なるためPHPのwpDataから受け取る)
 *
 * const ECMA_SLUG = window.wpData?.ecmaSlug || 'chokai';
 * const ECMA_BASE = `https://apply.ecmaonline.com/${ECMA_SLUG}/`;
 */
const ECMA_BASE = 'https://test2.elephant-web.jp/school1/';

/** テーマディレクトリのURI */
const THEME_URI = window.wpData?.themeUri || '';

/** 教習所の電話番号 */
const TEL = window.wpData?.tel || '';

/**
 * 月末日を取得する関数
 *
 * @param {number} year - 年
 * @param {number} month - 月
 * @returns {number} 月末日
 * @example
 * const endDay = getMonthEndDay(2026, 2); // 28
 */
const getMonthEndDay = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
};

/**
 * FormStateからURLを生成する関数
 *
 * @param {FormState} state - フォームの状態
 * @returns {string} 生成されたURL
 * @example
 * const url = buildURL({ license: 'AT', yy: 2026, mm: 4, ... });
 */
const buildURL = (state: FormState): string => {
    const params = new URLSearchParams({
        yy: String(state.yy),
        mm: String(state.mm),
        dd: String(state.dd),
        license: state.license,
        men: state.men > 0 ? String(state.men) : '',
        women: state.women > 0 ? String(state.women) : '',
        breakfast: String(state.breakfast),
        lunch: String(state.lunch),
        dinner: String(state.dinner),
        couple: String(state.couple),
        mode: 'html',
    });
    return `${ECMA_BASE}?${params.toString()}`;
};

/**
 * HTMLを取得してパースする関数
 *
 * @param {string} url - 取得するURL
 * @returns {Promise<Document>} パースされたHTMLドキュメント
 * @example
 * const doc = await fetchHTML('https://example.com');
 * const title = doc.querySelector('title')?.textContent;
 */
const fetchHTML = async (url: string): Promise<Document> => {
    const encodedUrl = encodeURIComponent(url);
    const response = await fetch(`/wp-json/tsurugaku/v1/fetch-html?url=${encodedUrl}`);
    const data = await response.json();

    if (!data.success) {
        throw new Error('HTMLの取得に失敗しました');
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(data.html, 'text/html');

    // scriptタグを削除
    doc.querySelectorAll('script').forEach((script) => script.remove());

    // onchange属性を削除
    doc.querySelectorAll('[onchange]').forEach((el) => el.removeAttribute('onchange'));

    return doc;
};

/**
 * 全角数字と括弧を半角に変換する関数(シンプル版)
 *
 * @param {string} str - 変換する文字列
 * @returns {string} 変換後の文字列
 */
const convertZenkakuToHankaku = (str: string): string => {
    const map: Record<string, string> = {
        '０': '0', '１': '1', '２': '2', '３': '3', '４': '4',
        '５': '5', '６': '6', '７': '7', '８': '8', '９': '9',
        '（': '(', '）': ')'
    };

    return str.replace(/[０-９（）]/g, (char) => map[char] || char);
};

/**
 * クエリ文字列からyy/mmを取り出す関数
 *
 * @param {string | null | undefined} query - "?yy=2026&mm=9&..." 形式の文字列
 * @returns {{ yy: number; mm: number } | null} 取り出せた年月。取り出せない場合はnull
 */
const parseYearMonthFromQuery = (query: string | null | undefined): { yy: number; mm: number } | null => {
    if (!query) return null;

    const params = new URLSearchParams(query.replace(/^[^?]*\?/, ''));
    const yy = Number(params.get('yy'));
    const mm = Number(params.get('mm'));

    if (!yy || !mm) return null;

    return { yy, mm };
};

/**
 * エクマのHTMLから料金プランの月選択肢を取り出す関数
 *
 * @param {Document} doc - エクマから取得したHTMLドキュメント
 * @returns {PlanMonth[]} 月選択肢の配列。取り出せない場合は空配列
 */
const parsePlanMonths = (doc: Document): PlanMonth[] => {
    const select = doc.querySelector('select[name="select-num"]');
    if (!select) return [];

    return Array.from(select.querySelectorAll('option')).flatMap((option) => {
        const yearMonth = parseYearMonthFromQuery(option.getAttribute('value'));
        if (!yearMonth) return [];

        return [{
            ...yearMonth,
            label: option.textContent?.trim() || `${yearMonth.yy}年${yearMonth.mm}月のプラン`,
        }];
    });
};

/**
 * エクマのHTMLから前月/翌月リンクの年月を取り出す関数
 *
 * @param {Document | null} doc - エクマから取得したHTMLドキュメント
 * @param {string} selector - リンクのセレクタ
 * @returns {{ yy: number; mm: number } | null} 取り出せた年月。取り出せない場合はnull
 */
const parseNavYearMonth = (doc: Document | null, selector: string): { yy: number; mm: number } | null => {
    if (!doc) return null;

    return parseYearMonthFromQuery(doc.querySelector(selector)?.getAttribute('href'));
};

/**
 * 申込みリンクのURLを組み立てる関数
 *
 * エクマ側のhrefが相対・絶対どちらでも正しく解決する
 *
 * @param {string | null | undefined} href - エクマのHTMLに書かれたhref
 * @returns {string} 申込みページのURL
 * @example
 * resolveApplyURL('form/?sid=1') // "https://apply.ecmaonline.com/chokai/form/?sid=1"
 */
const resolveApplyURL = (href: string | null | undefined): string => {
    if (!href) return ECMA_BASE;

    try {
        return new URL(href, ECMA_BASE).href;
    } catch {
        return ECMA_BASE;
    }
};

/**
 * 指定年月の1ヶ月前後を求める関数
 *
 * @param {number} yy - 年
 * @param {number} mm - 月
 * @param {number} diff - ずらす月数(-1で前月、1で翌月)
 * @returns {{ yy: number; mm: number }} ずらした後の年月
 */
const shiftYearMonth = (yy: number, mm: number, diff: number): { yy: number; mm: number } => {
    const base = new Date(yy, mm - 1 + diff, 1);
    return { yy: base.getFullYear(), mm: base.getMonth() + 1 };
};

/**
 * フォームの初期状態を取得する関数
 *
 * @returns {FormState} 初期状態
 */
const getInitialFormState = (): FormState => {
    const now = new Date();
    return {
        license: 'AT',
        yy: now.getFullYear(),
        mm: now.getMonth() + 1,
        dd: 0,
        men: 0,
        women: 0,
        breakfast: false,
        lunch: false,
        dinner: false,
        couple: false,
    };
};

/**
 * メインのReactコンポーネント
 */
const App = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [htmlDoc, setHtmlDoc] = useState<Document | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(getInitialFormState);

    // 詳細検索条件(アコーディオン)の開閉
    const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);

    // 実際に表示されている料金プランの年月
    const [displayedYearMonth, setDisplayedYearMonth] = useState<{ yy: number; mm: number }>(() => {
        const now = new Date();
        return { yy: now.getFullYear(), mm: now.getMonth() + 1 };
    });

    // 検索完了後に料金プランへスクロールするかどうか
    const shouldScrollRef = useRef<boolean>(false);

    // 通信の追い越しで古い結果が表示されるのを防ぐための番号
    const requestIdRef = useRef<number>(0);

    const planRef = useRef<HTMLDivElement>(null);

    // カップル選択可能かどうか
    const canSelectCouple = useMemo(() => {
        return formState.men + formState.women === 2;
    }, [formState.men, formState.women]);

    // 日のオプションを生成
    const dayOptions = useMemo(() => {
        const endDay = getMonthEndDay(formState.yy, formState.mm);
        const options = [{ value: 0, label: '-' }];
        for (let i = 1; i <= endDay; i++) {
            options.push({ value: i, label: String(i) });
        }
        return options;
    }, [formState.yy, formState.mm]);

    // 年のオプションを生成
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return [currentYear, currentYear + 1, currentYear + 2].map((y) => ({
            value: y,
            label: String(y),
        }));
    }, []);

    // 月のオプションを生成
    const monthOptions = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => ({
            value: i + 1,
            label: String(i + 1),
        }));
    }, []);

    // 人数のオプションを生成
    const peopleOptions = useMemo(() => {
        return [
            { value: 0, label: '-' },
            { value: 1, label: '1人' },
            { value: 2, label: '2人' },
            { value: 3, label: '3人' },
            { value: 4, label: '4人' },
            { value: 5, label: '5人' },
            { value: 6, label: '6人' },
        ];
    }, []);

    // 料金プラン月選択のオプションを生成(エクマが返した選択肢に追従する)
    const planMonthOptions = useMemo(() => {
        const parsed = htmlDoc ? parsePlanMonths(htmlDoc) : [];
        const options = parsed.length > 0
            ? parsed
            : Array.from({ length: 12 }, (_, i) => ({
                yy: displayedYearMonth.yy,
                mm: i + 1,
                label: `${displayedYearMonth.yy}年${i + 1}月のプラン`,
            }));

        // 前月/翌月で選択肢の範囲外へ移動した場合も表示中の月を選べるようにする
        const hasDisplayed = options.some(
            (option) => option.yy === displayedYearMonth.yy && option.mm === displayedYearMonth.mm
        );
        if (hasDisplayed) return options;

        return [
            {
                ...displayedYearMonth,
                label: `${displayedYearMonth.yy}年${displayedYearMonth.mm}月のプラン`,
            },
            ...options,
        ];
    }, [htmlDoc, displayedYearMonth]);

    // プランの一覧
    const planItems = useMemo(() => {
        if (!htmlDoc) return [];
        return Array.from(htmlDoc.querySelectorAll('.plan-box-date .plan-box-date-item'));
    }, [htmlDoc]);

    // プラン枠自体が取れていない場合はエクマ側の取得失敗とみなす
    const isPlanUnavailable = useMemo(() => {
        return !!htmlDoc && !htmlDoc.querySelector('.plan-box');
    }, [htmlDoc]);

    /**
     * HTMLを取得して解析
     */
    const loadHTML = useCallback(async (state: FormState) => {
        const requestId = ++requestIdRef.current;

        try {
            setLoading(true);
            setError(null);

            const url = buildURL(state);
            const doc = await fetchHTML(url);

            // 古い通信の結果は捨てる
            if (requestId !== requestIdRef.current) return;

            setHtmlDoc(doc);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;

            console.error('エラー発生:', err);
            setError(err instanceof Error ? err.message : '不明なエラー');
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    // 初回ロード
    useEffect(() => {
        loadHTML(formState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 検索実行後に料金プランへスクロール
    useEffect(() => {
        if (loading || !shouldScrollRef.current) return;

        shouldScrollRef.current = false;

        if (error) return;

        planRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [loading, error]);

    /**
     * フォームの値を更新する関数
     */
    const updateFormState = useCallback((updates: Partial<FormState>) => {
        setFormState((prev) => {
            const newState = { ...prev, ...updates };

            // カップル選択不可の場合はfalseにする
            if (newState.men + newState.women !== 2) {
                newState.couple = false;
            }

            // 日が月末日を超える場合は0にリセット
            const endDay = getMonthEndDay(newState.yy, newState.mm);
            if (newState.dd > endDay) {
                newState.dd = 0;
            }

            return newState;
        });
    }, []);

    /**
     * 検索ボタンを押したとき
     */
    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        shouldScrollRef.current = true;
        setDisplayedYearMonth({ yy: formState.yy, mm: formState.mm });
        loadHTML(formState);
    }, [formState, loadHTML]);

    /**
     * 表示する年月を変更する関数
     */
    const changeDisplayedYearMonth = useCallback((yy: number, mm: number) => {
        const newState = { ...formState, yy, mm, dd: 0 };
        setFormState(newState);
        setDisplayedYearMonth({ yy, mm });
        loadHTML(newState);
    }, [formState, loadHTML]);

    /**
     * 前月ボタンを押したとき
     */
    const handlePrevMonth = useCallback(() => {
        const target = parseNavYearMonth(htmlDoc, '.calender-ttl-prev')
            ?? shiftYearMonth(displayedYearMonth.yy, displayedYearMonth.mm, -1);
        changeDisplayedYearMonth(target.yy, target.mm);
    }, [htmlDoc, displayedYearMonth, changeDisplayedYearMonth]);

    /**
     * 翌月ボタンを押したとき
     */
    const handleNextMonth = useCallback(() => {
        const target = parseNavYearMonth(htmlDoc, '.calender-ttl-next')
            ?? shiftYearMonth(displayedYearMonth.yy, displayedYearMonth.mm, 1);
        changeDisplayedYearMonth(target.yy, target.mm);
    }, [htmlDoc, displayedYearMonth, changeDisplayedYearMonth]);

    /**
     * 料金プランの月セレクトボックスが変わったとき
     */
    const handlePlanMonthChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const [yy, mm] = e.target.value.split('-').map(Number);
        if (!yy || !mm) return;

        changeDisplayedYearMonth(yy, mm);
    }, [changeDisplayedYearMonth]);

    return (
        <>
            {loading && !htmlDoc && <p>読み込み中...</p>}
            {error && <p style={{ color: 'red' }}>エラー: {error}</p>}
            {htmlDoc && (
                <>
                    <form onSubmit={handleSubmit}>
                        <div className="gy__innerReactSearch">
                            <div className="gy__innerReactSearchBoxes">
                                {/* 取得希望免許 */}
                                <div className="gy__innerReactSearchBoxesBox">
                                    <div className="gy__innerReactSearchBoxesBoxTtl">
                                        <picture>
                                            <img src={`${THEME_URI}/lib/imgs/ico_directions_car.svg`} alt="" />
                                        </picture>
                                        <p>取得希望免許を選択</p>
                                    </div>
                                    <div className="gy__innerReactSearchBoxesBoxType type-select">
                                        <select
                                            name="license"
                                            className="select"
                                            value={formState.license}
                                            onChange={(e) => updateFormState({ license: e.target.value })}
                                        >
                                            <option value="AT">普通自動車AT</option>
                                            <option value="MT">普通自動車MT</option>
                                        </select>
                                    </div>
                                </div>

                                {/* 希望入校月 */}
                                <div className="gy__innerReactSearchBoxesBox">
                                    <div className="gy__innerReactSearchBoxesBoxTtl">
                                        <picture>
                                            <img src={`${THEME_URI}/lib/imgs/ico_calendar.svg`} alt="" />
                                        </picture>
                                        <p>希望入校月を選択</p>
                                    </div>
                                    <div className="gy__innerReactSearchBoxesBoxDate">
                                        <div className="gy__innerReactSearchBoxesBoxDateCell">
                                            <div className="gy__innerReactSearchBoxesBoxDateCellInput type-select">
                                                <select
                                                    name="yy"
                                                    className="select"
                                                    value={formState.yy}
                                                    onChange={(e) => updateFormState({ yy: Number(e.target.value) })}
                                                >
                                                    {yearOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <span>年</span>
                                        </div>
                                        <br className='mq4sp' />
                                        <div className="gy__innerReactSearchBoxesBoxDateCell">
                                            <div className="gy__innerReactSearchBoxesBoxDateCellInput type-select">
                                                <select
                                                    name="mm"
                                                    className="select"
                                                    value={formState.mm}
                                                    onChange={(e) => updateFormState({ mm: Number(e.target.value) })}
                                                >
                                                    {monthOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <span>月</span>
                                        </div>
                                        <div className="gy__innerReactSearchBoxesBoxDateCell is-suffix">
                                            <span>のプラン</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 詳細検索条件の開閉 */}
                            <div className="gy__innerReactSearchToggle">
                                <p>検索条件を追加する</p>
                                <button
                                    type="button"
                                    className={`gy__innerReactSearchToggleBtn ${isDetailOpen ? 'is-open' : ''}`}
                                    aria-expanded={isDetailOpen}
                                    aria-controls="gy-search-detail"
                                    aria-label={isDetailOpen ? '検索条件を閉じる' : '検索条件を追加する'}
                                    onClick={() => setIsDetailOpen((prev) => !prev)}
                                ></button>
                            </div>

                            <div
                                id="gy-search-detail"
                                className={`gy__innerReactSearchDetail ${isDetailOpen ? 'is-open' : ''}`}
                                inert={!isDetailOpen}
                            >
                                <div className="gy__innerReactSearchDetailInner">
                                    <div className="gy__innerReactSearchBoxes">
                                        {/* 入校日 */}
                                        <div className="gy__innerReactSearchBoxesBox">
                                            <div className="gy__innerReactSearchBoxesBoxTtl">
                                                <picture>
                                                    <img src={`${THEME_URI}/lib/imgs/ico_calendar_check.svg`} alt="" />
                                                </picture>
                                                <p>入校日を指定する</p>
                                            </div>
                                            <div className="gy__innerReactSearchBoxesBoxDate is-day">
                                                <div className="gy__innerReactSearchBoxesBoxDateCell">
                                                    <div className="gy__innerReactSearchBoxesBoxDateCellInput type-select">
                                                        <select
                                                            name="dd"
                                                            className="select"
                                                            value={formState.dd}
                                                            onChange={(e) => updateFormState({ dd: Number(e.target.value) })}
                                                        >
                                                            {dayOptions.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <span>日</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 食事 */}
                                        <div className="gy__innerReactSearchBoxesBox">
                                            <div className="gy__innerReactSearchBoxesBoxTtl">
                                                <picture>
                                                    <img src={`${THEME_URI}/lib/imgs/ico_fork_spoon.svg`} alt="" />
                                                </picture>
                                                <p>食事</p>
                                            </div>
                                            <div className="gy__innerReactSearchBoxesBoxMeal">
                                                <div>
                                                    <label className="checkbox-label" htmlFor="breakfast">
                                                        <input
                                                            id="breakfast"
                                                            type="checkbox"
                                                            name="breakfast"
                                                            checked={formState.breakfast}
                                                            onChange={(e) => updateFormState({ breakfast: e.target.checked })}
                                                        />
                                                        朝食
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="checkbox-label" htmlFor="lunch">
                                                        <input
                                                            id="lunch"
                                                            type="checkbox"
                                                            name="lunch"
                                                            checked={formState.lunch}
                                                            onChange={(e) => updateFormState({ lunch: e.target.checked })}
                                                        />
                                                        昼食
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="checkbox-label" htmlFor="dinner">
                                                        <input
                                                            id="dinner"
                                                            type="checkbox"
                                                            name="dinner"
                                                            checked={formState.dinner}
                                                            onChange={(e) => updateFormState({ dinner: e.target.checked })}
                                                        />
                                                        夕食
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 人数 */}
                                        <div className="gy__innerReactSearchBoxesBox is-full">
                                            <div className="gy__innerReactSearchBoxesBoxTtl">
                                                <picture>
                                                    <img src={`${THEME_URI}/lib/imgs/ico_person.svg`} alt="" />
                                                </picture>
                                                <p>人数</p>
                                            </div>
                                            <div className="gy__innerReactSearchBoxesBoxPeople">
                                                <div className="gy__innerReactSearchBoxesBoxPeopleCount">
                                                    <div className="gy__innerReactSearchBoxesBoxPeopleCountCell">
                                                        <p>男性</p>
                                                        <div className="gy__innerReactSearchBoxesBoxPeopleCountCellInput type-select">
                                                            <select
                                                                name="men"
                                                                className="select"
                                                                value={formState.men}
                                                                onChange={(e) => updateFormState({ men: Number(e.target.value) })}
                                                            >
                                                                {peopleOptions.map((opt) => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="gy__innerReactSearchBoxesBoxPeopleCountCell">
                                                        <p>女性</p>
                                                        <div className="gy__innerReactSearchBoxesBoxPeopleCountCellInput type-select">
                                                            <select
                                                                name="women"
                                                                className="select"
                                                                value={formState.women}
                                                                onChange={(e) => updateFormState({ women: Number(e.target.value) })}
                                                            >
                                                                {peopleOptions.map((opt) => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="gy__innerReactSearchBoxesBoxPeopleCouple">
                                                    <label className="checkbox-label" htmlFor="couple">
                                                        <input
                                                            id="couple"
                                                            type="checkbox"
                                                            name="couple"
                                                            checked={formState.couple}
                                                            disabled={!canSelectCouple}
                                                            onChange={(e) => updateFormState({ couple: e.target.checked })}
                                                        />
                                                        <span className={`couple_txt ${canSelectCouple ? 'couple_active' : ''}`}>カップル</span>
                                                    </label>
                                                </div>
                                                <div className="gy__innerReactSearchBoxesBoxPeopleAttention">
                                                    <p>6名以上は教習所へ直接お電話ください{TEL && <><br />{TEL}</>}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button className="form-search-btn type-btn" type="submit">検索</button>
                        </div>
                    </form>

                    <div className={`gy__innerReactPlan ${loading ? 'is-loading' : ''}`} ref={planRef}>
                        <div className="gy__innerReactPlanTtl">
                            <p>料金プラン</p>
                        </div>
                        <div className="gy__innerReactPlanTable">
                            <div className="gy__innerReactPlanTableCtrls">
                                <button type="button" onClick={handlePrevMonth}>
                                    前月
                                </button>
                                <div className="gy__innerReactPlanTableCtrlsSelect type-select">
                                    <select
                                        name="select-num"
                                        className="select"
                                        value={`${displayedYearMonth.yy}-${displayedYearMonth.mm}`}
                                        onChange={handlePlanMonthChange}
                                    >
                                        {planMonthOptions.map((opt) => (
                                            <option key={`${opt.yy}-${opt.mm}`} value={`${opt.yy}-${opt.mm}`}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <button type="button" onClick={handleNextMonth}>
                                    翌月
                                </button>
                            </div>
                            <div className="gy__innerReactPlanTableCnt">
                                {
                                    planItems.length > 0 ? <div className='gy__innerReactPlanTableCntRows'>
                                        {
                                            planItems.map((elm, elmIndex) => <div className='gy__innerReactPlanTableCntRowsRow' key={elmIndex}>
                                                <div className="gy__innerReactPlanTableCntRowsRowDate">
                                                    <p dangerouslySetInnerHTML={{ __html: convertZenkakuToHankaku(elm.querySelector(`dt`)?.innerHTML || '') }}></p>
                                                </div>
                                                <div className="gy__innerReactPlanTableCntRowsRowPlans">
                                                    {
                                                        Array.from(elm?.querySelectorAll(`.plan-cal-list-item`)).map((plan, planIndex) =>
                                                            <div className="gy__innerReactPlanTableCntRowsRowPlansPlan" key={planIndex}>
                                                                <div className="gy__innerReactPlanTableCntRowsRowPlansPlanInfo">
                                                                    <div className="gy__innerReactPlanTableCntRowsRowPlansPlanInfoUpper">
                                                                        {
                                                                            plan.querySelector(`.plan-cal-list-item-txt-name`) && <p className='name' dangerouslySetInnerHTML={{ __html: plan.querySelector(`.plan-cal-list-item-txt-name`)?.innerHTML || '' }}></p>
                                                                        }
                                                                        {
                                                                            plan.querySelector(`.plan-cal-list-item-txt-gur`) && <p className='gur' dangerouslySetInnerHTML={{ __html: plan.querySelector(`.plan-cal-list-item-txt-gur`)?.innerHTML || '' }}></p>
                                                                        }
                                                                        {
                                                                            plan.querySelector(`.plan-cal-list-item-txt-amo`) && <p className='amo' dangerouslySetInnerHTML={{ __html: plan.querySelector(`.plan-cal-list-item-txt-amo`)?.innerHTML || '' }}></p>
                                                                        }
                                                                    </div>
                                                                    <div className="gy__innerReactPlanTableCntRowsRowPlansPlanInfoTags">
                                                                        {
                                                                            plan.querySelector(`.plan-cal-list-item-txt-meal`) && <div className='meal' dangerouslySetInnerHTML={{
                                                                                __html: `<picture>
                                        <img src="${THEME_URI}/lib/imgs/ico_fork_spoon.svg" alt="" />
                                    </picture><span class="innerhtml">${plan.querySelector(`.plan-cal-list-item-txt-meal`)?.innerHTML}</span>`
                                                                            }}></div>
                                                                        }
                                                                        {
                                                                            plan.querySelector(`.plan-cal-list-item-txt-gender`) && <div className='gender' dangerouslySetInnerHTML={{
                                                                                __html: `<picture>
                                        <img src="${THEME_URI}/lib/imgs/ico_person.svg" alt="" />
                                    </picture><span class="innerhtml">${plan.querySelector(`.plan-cal-list-item-txt-gender`)?.innerHTML}</span>`
                                                                            }}></div>
                                                                        }
                                                                    </div>
                                                                </div>
                                                                <div className="gy__innerReactPlanTableCntRowsRowPlansPlanApply">
                                                                    <a href={resolveApplyURL(plan?.querySelector(`a`)?.getAttribute('href'))} target='_blank' className='type-btn'>
                                                                        申込み
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>)
                                        }
                                    </div> : <p>{isPlanUnavailable ? 'プラン情報を取得できませんでした。時間をおいてお試しください。' : '該当月のプランはありません。'}</p>
                                }
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default App;
